import {
  AlertCircleIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  RadioCardGroup,
  type RadioCardOption,
} from '@/components/ui/radio-card-group';
import { cn } from '@/lib/utils';

/**
 * One assignable target in an {@link AssignmentList}. Generic over the id type
 * so callers keep their own union (e.g. an app-URL string literal union).
 */
export interface AssignmentListItem<TId extends string> {
  /** Stable, unique id for the row (drives accordion value + mode lookup). */
  id: TId;
  /** Primary label shown on the row trigger (e.g. the target URL). */
  label: string;
  /** Optional secondary label shown beneath the primary label. */
  sublabel?: string;
}

/** Per-row tri-state assignment mode. */
export type AssignmentRowMode = 'off' | 'shared' | 'custom';

/** Resolution state of a single row, surfaced as a non-color-only indicator. */
export type AssignmentRowStatus = 'na' | 'incomplete' | 'ready';

export interface AssignmentListProps<TId extends string> {
  /** Rows to render, one per target. */
  items: AssignmentListItem<TId>[];
  /** Current mode per row id. Missing ids are treated as `'off'`. */
  mode: Record<TId, AssignmentRowMode>;
  /** Called when a row's mode changes. */
  onModeChange: (id: TId, next: AssignmentRowMode) => void;
  /** The currently expanded row, or `null` when all rows are collapsed. */
  expandedId: TId | null;
  /** Called when expansion changes (the new row id, or `null` when collapsed). */
  onExpandedChange: (id: TId | null) => void;
  /** Renders the per-row editor body; only invoked for the expanded row. */
  renderEditor: (id: TId) => React.ReactNode;
  /** Optional per-row status used to render an accessible (non-color) indicator. */
  rowStatus?: (id: TId) => AssignmentRowStatus;
  /** Optional content rendered in the header region (e.g. a shared-config card). */
  sharedSlot?: React.ReactNode;
  /** Optional content rendered in the header region (e.g. bulk "Set all" actions). */
  bulkActions?: React.ReactNode;
  /** When `false`, the per-row "Shared" mode option is omitted. Defaults to `true`. */
  enableShared?: boolean;
  /**
   * Accessible name for a row's mode radiogroup. Kept generic so this primitive
   * stays domain-agnostic; defaults to `Mode for ${item.label}`. Consumers with
   * a specific domain (e.g. credentials) pass their own label here.
   */
  modeAriaLabel?: (item: AssignmentListItem<TId>) => string;
  /** Accessible name for the list container. */
  ariaLabel: string;
  /** Extra classes merged onto the outer container. */
  className?: string;
}

const STATUS_META: Record<
  AssignmentRowStatus,
  { icon: LucideIcon; label: string; tone: string }
> = {
  ready: {
    icon: CheckCircle2Icon,
    label: 'Ready',
    tone: 'text-chariot-stroke-info',
  },
  incomplete: {
    icon: AlertCircleIcon,
    label: 'Incomplete',
    tone: 'text-chariot-stroke-high',
  },
  na: {
    icon: CircleDashedIcon,
    label: 'Not configured',
    tone: 'text-chariot-text-secondary',
  },
};

/**
 * Status dot: an icon (color cue) PLUS `sr-only` text (the accessible label) so
 * the state is never communicated by color alone (WCAG 1.4.1).
 */
function RowStatusIndicator({ status }: { status: AssignmentRowStatus }) {
  const { icon: Icon, label, tone } = STATUS_META[status];
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <Icon aria-hidden="true" className={cn('size-4', tone)} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

const MODE_LABELS: Record<AssignmentRowMode, string> = {
  off: 'Off',
  shared: 'Shared',
  custom: 'Custom',
};

function AssignmentListInner<TId extends string>(
  {
    items,
    mode,
    onModeChange,
    expandedId,
    onExpandedChange,
    renderEditor,
    rowStatus,
    sharedSlot,
    bulkActions,
    enableShared = true,
    modeAriaLabel = item => `Mode for ${item.label}`,
    ariaLabel,
    className,
  }: AssignmentListProps<TId>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const modeOptions: RadioCardOption<AssignmentRowMode>[] = (
    enableShared
      ? (['off', 'shared', 'custom'] as const)
      : (['off', 'custom'] as const)
  ).map(value => ({ value, label: MODE_LABELS[value] }));

  const hasHeader = Boolean(sharedSlot ?? bulkActions);

  return (
    <div
      ref={ref}
      role="group"
      aria-label={ariaLabel}
      className={cn('flex flex-col gap-4', className)}
    >
      {hasHeader && (
        <div className="flex flex-col gap-3">
          {bulkActions}
          {sharedSlot}
        </div>
      )}

      <Accordion
        type="single"
        collapsible
        value={expandedId ?? ''}
        onValueChange={value =>
          onExpandedChange(value === '' ? null : (value as TId))
        }
        className="border-chariot-stroke divide-chariot-stroke divide-y rounded-md border"
      >
        {items.map(item => {
          const rowMode = mode[item.id] ?? 'off';
          const isExpanded = expandedId === item.id;
          const status = rowStatus?.(item.id);

          return (
            <AccordionItem
              key={item.id}
              value={item.id}
              className="border-chariot-stroke border-b-0"
            >
              <div className="flex flex-col gap-3 px-4 py-3">
                <AccordionTrigger className="py-0 hover:no-underline">
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <span className="flex min-w-0 flex-col text-left">
                      <span className="text-chariot-text-primary truncate text-sm font-medium">
                        {item.label}
                      </span>
                      {item.sublabel && (
                        <span className="text-chariot-text-secondary truncate text-xs">
                          {item.sublabel}
                        </span>
                      )}
                    </span>
                    {status && <RowStatusIndicator status={status} />}
                  </span>
                </AccordionTrigger>

                <RadioCardGroup<AssignmentRowMode>
                  value={rowMode}
                  onChange={next => onModeChange(item.id, next)}
                  options={modeOptions}
                  ariaLabel={modeAriaLabel(item)}
                  className={cn(
                    'gap-2',
                    enableShared ? 'grid-cols-3' : 'grid-cols-2'
                  )}
                />
              </div>

              <AccordionContent className="px-4 pt-0 pb-4">
                {isExpanded ? renderEditor(item.id) : null}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

/**
 * AssignmentList — a generic, STANDARDIZE-CANDIDATE primitive for batch-applying
 * a config to N targets with a per-row Off/Shared/Custom mode control and an
 * expandable per-row editor (accordion-of-one).
 *
 * Composes existing primitives: `Accordion` (single/collapsible, driven by
 * `expandedId`/`onExpandedChange`) and `RadioCardGroup` for the per-row mode
 * control (true `role="radiogroup"` + roving tabindex + ≥44px targets). Row
 * status is rendered as an icon plus `sr-only` text so state is never conveyed
 * by color alone. Token-only styling; no credential-specific logic.
 *
 * forwardRef wrapper preserves the generic `TId` parameter (a plain forwardRef
 * erases generics). The cast keeps call-site inference.
 */
export const AssignmentList = React.forwardRef(AssignmentListInner) as <
  TId extends string,
>(
  props: AssignmentListProps<TId> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => React.ReactElement;
